import type { OfflineCatalogSnapshot, OfflineProduct } from './types.js';

export interface LocalProductWithStock extends OfflineProduct {
  stock: number;
  exposedOnSite: number;
  availableStock: number;
}

export interface LocalCategory {
  visibleId: string;
  name: string;
  isActive: boolean;
  isFreeSaleOrService: boolean;
  isFreePrice: boolean;
  productsCount: number;
}

export interface PaginatedLocalProducts {
  products: LocalProductWithStock[];
  total: number;
  hasMore: boolean;
}

function inventoryFor(
  catalog: OfflineCatalogSnapshot,
  productId: string,
  branchId: string,
) {
  return catalog.inventories.find(
    (row) => row.productId === productId && row.branchId === branchId,
  );
}

export function enrichProductWithStock(
  product: OfflineProduct,
  catalog: OfflineCatalogSnapshot,
  branchId: string,
): LocalProductWithStock {
  const inv = inventoryFor(catalog, product.visibleId, branchId);
  const stock = inv?.stock ?? 0;
  const exposedOnSite = inv?.exposedOnSite ?? stock;
  return {
    ...product,
    stock,
    exposedOnSite,
    availableStock: Math.max(0, exposedOnSite),
  };
}

export function getLocalCategories(
  catalog: OfflineCatalogSnapshot | null,
): LocalCategory[] {
  if (!catalog) return [];
  const activeProducts = catalog.products.filter((p) => p.isActive);
  return catalog.categories
    .filter((c) => c.isActive)
    .map((c) => ({
      visibleId: c.visibleId,
      name: c.name,
      isActive: c.isActive,
      isFreeSaleOrService: c.isFreeSaleOrService === true,
      isFreePrice: c.isFreePrice === true,
      productsCount: activeProducts.filter((p) => p.categoryId === c.visibleId).length,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
}

function paginate<T>(items: T[], page: number, limit: number): PaginatedLocalProducts {
  const total = items.length;
  const offset = Math.max(0, (page - 1) * limit);
  const slice = items.slice(offset, offset + limit);
  return {
    products: slice as LocalProductWithStock[],
    total,
    hasMore: offset + slice.length < total,
  };
}

export function getProductsByCategory(
  catalog: OfflineCatalogSnapshot | null,
  categoryId: string,
  branchId: string,
  page = 1,
  limit = 50,
): PaginatedLocalProducts {
  if (!catalog) {
    return { products: [], total: 0, hasMore: false };
  }
  const filtered = catalog.products
    .filter((p) => p.isActive && p.categoryId === categoryId)
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
    .map((p) => enrichProductWithStock(p, catalog, branchId));
  return paginate(filtered, page, limit);
}

/** All active catalog products for a branch — stock 0 when no inventory row (matches FO web). */
export function getAllBranchProducts(
  catalog: OfflineCatalogSnapshot | null,
  branchId: string,
  page = 1,
  limit = 100,
): PaginatedLocalProducts {
  if (!catalog) {
    return { products: [], total: 0, hasMore: false };
  }
  const filtered = catalog.products
    .filter((p) => p.isActive)
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
    .map((p) => enrichProductWithStock(p, catalog, branchId));
  return paginate(filtered, page, limit);
}

export function searchLocalCatalogProducts(
  catalog: OfflineCatalogSnapshot | null,
  query: string,
  branchId: string,
): LocalProductWithStock[] {
  if (!catalog) return [];
  const q = query.trim().toLowerCase();
  const base = catalog.products.filter((p) => p.isActive);
  const matched =
    !q
      ? base.slice(0, 50)
      : base.filter((p) => {
          const tokens = q.split(/\s+/).filter(Boolean);
          const hay = [p.name, p.sku, p.barcode || '', p.description || '']
            .join(' ')
            .toLowerCase();
          return tokens.every((t) => hay.includes(t));
        });
  return matched.map((p) => enrichProductWithStock(p, catalog, branchId));
}
